import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BLOCK_TYPES = [
  { value: 'vacaciones_doctor', label: 'Vacaciones Doctor', color: '#8B5CF6', needsDoctor: true },
  { value: 'ausencia_doctor', label: 'Ausencia Doctor', color: '#EC4899', needsDoctor: true },
  { value: 'feriado', label: 'Feriado', color: '#DC2626' },
  { value: 'bloqueo_pabellon', label: 'Bloqueo Pabellón', color: '#F59E0B', needsPavilion: true },
  { value: 'otro', label: 'Otro (personalizado)', color: '#6B7280' },
];

export function BlockDialog({ open, onClose, block, slot }) {
  const [doctors, setDoctors] = useState([]);
  const [pavilions, setPavilions] = useState([]);
  const [formData, setFormData] = useState({
    block_type: 'vacaciones_doctor',
    custom_type: '',
    title: '',
    doctor_id: '',
    pavilion: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    all_day: true,
    color: '#8B5CF6',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDoctors();
    fetchPavilions();
  }, []);

  const fetchPavilions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pavilions`, { withCredentials: true });
      setPavilions(data.filter((p) => p.is_active !== false));
    } catch (err) {
      console.error('Error fetching pavilions:', err);
    }
  };

  useEffect(() => {
    if (block) {
      const isKnown = DEFAULT_BLOCK_TYPES.some(t => t.value === block.block_type && t.value !== 'otro');
      setFormData({
        block_type: isKnown ? block.block_type : 'otro',
        custom_type: isKnown ? '' : block.block_type,
        title: block.title || '',
        doctor_id: block.doctor_id || '',
        pavilion: block.pavilion || '',
        start_date: block.start_date || '',
        end_date: block.end_date || '',
        start_time: block.start_time || '',
        end_time: block.end_time || '',
        all_day: block.all_day !== false,
        color: block.color || '#8B5CF6',
      });
    } else if (slot) {
      const startDate = slot.start.toISOString().split('T')[0];
      const endDate = slot.end ? slot.end.toISOString().split('T')[0] : startDate;
      setFormData(prev => ({
        ...prev,
        start_date: startDate,
        end_date: endDate,
        start_time: slot.start.toTimeString().slice(0, 5),
        end_time: slot.end ? slot.end.toTimeString().slice(0, 5) : '',
      }));
    }
  }, [block, slot]);

  const fetchDoctors = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setDoctors(data.filter(u => u.role === 'doctor'));
    } catch (err) {
      console.error('Error fetching doctors:', err);
    }
  };

  const handleTypeChange = (type) => {
    const bt = DEFAULT_BLOCK_TYPES.find(t => t.value === type);
    setFormData(prev => ({
      ...prev,
      block_type: type,
      color: bt?.color || prev.color,
      custom_type: type === 'otro' ? prev.custom_type : '',
      doctor_id: bt?.needsDoctor ? prev.doctor_id : '',
      pavilion: bt?.needsPavilion ? prev.pavilion : '',
      all_day: type === 'feriado' ? true : prev.all_day,
    }));
  };

  const currentType = DEFAULT_BLOCK_TYPES.find(t => t.value === formData.block_type);
  const needsDoctor = currentType?.needsDoctor || false;
  const needsPavilion = currentType?.needsPavilion || false;
  const isCustom = formData.block_type === 'otro';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const actualType = isCustom ? (formData.custom_type || 'otro') : formData.block_type;

      const payload = {
        block_type: actualType,
        title: formData.title,
        doctor_id: needsDoctor ? formData.doctor_id : '',
        pavilion: needsPavilion ? formData.pavilion : '',
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_time: formData.all_day ? '' : formData.start_time,
        end_time: formData.all_day ? '' : formData.end_time,
        all_day: formData.all_day,
        color: formData.color,
      };

      // Auto-generate title if empty
      if (!payload.title) {
        if (needsDoctor && payload.doctor_id) {
          const doc = doctors.find(d => d.id === payload.doctor_id);
          const typeLabel = formData.block_type === 'vacaciones_doctor' ? 'Vacaciones' : 'No está';
          payload.title = `${doc?.name || 'Doctor'} ${typeLabel}`;
        } else if (formData.block_type === 'feriado') {
          payload.title = 'Feriado';
        } else if (needsPavilion) {
          payload.title = `Bloqueo ${payload.pavilion || 'Pabellón'}`;
        } else if (isCustom) {
          payload.title = formData.custom_type || 'Bloqueo personalizado';
        }
      }

      if (block?.id) {
        await axios.put(`${API_URL}/api/blocks/${block.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/blocks`, payload, { withCredentials: true });
      }
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E8E5DD] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl text-[#1A1C18]">
            {block ? 'Editar Bloqueo' : 'Nuevo Bloqueo'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Block Type */}
          <div>
            <Label>Tipo de bloqueo</Label>
            <Select value={formData.block_type} onValueChange={handleTypeChange}>
              <SelectTrigger data-testid="block-type-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEFAULT_BLOCK_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom type name */}
          {isCustom && (
            <div>
              <Label>Nombre del tipo</Label>
              <Input
                data-testid="block-custom-type"
                value={formData.custom_type}
                onChange={(e) => setFormData({ ...formData, custom_type: e.target.value })}
                placeholder="Ej: Reunión, Capacitación, Mantención..."
                required
                className="mt-1 bg-transparent border-[#E8E5DD]"
              />
            </div>
          )}

          {/* Title */}
          <div>
            <Label>Título (opcional, se genera automáticamente)</Label>
            <Input
              data-testid="block-title-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ej: Dr. Terré Vacaciones"
              className="mt-1 bg-transparent border-[#E8E5DD]"
            />
          </div>

          {/* Doctor selector */}
          {needsDoctor && (
            <div>
              <Label>Doctor</Label>
              <Select value={formData.doctor_id} onValueChange={(v) => setFormData({ ...formData, doctor_id: v })}>
                <SelectTrigger data-testid="block-doctor-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                  <SelectValue placeholder="Seleccionar doctor" />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Pavilion selector */}
          {needsPavilion && (
            <div>
              <Label>Pabellón</Label>
              <Select value={formData.pavilion} onValueChange={(v) => setFormData({ ...formData, pavilion: v })}>
                <SelectTrigger data-testid="block-pavilion-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                  <SelectValue placeholder="Seleccionar pabellón" />
                </SelectTrigger>
                <SelectContent>
                  {pavilions.map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha inicio</Label>
              <Input
                type="date"
                data-testid="block-start-date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value, end_date: formData.end_date || e.target.value })}
                required
                className="mt-1 bg-transparent border-[#E8E5DD]"
              />
            </div>
            <div>
              <Label>Fecha fin</Label>
              <Input
                type="date"
                data-testid="block-end-date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
                className="mt-1 bg-transparent border-[#E8E5DD]"
              />
            </div>
          </div>

          {/* All day toggle */}
          {formData.block_type !== 'feriado' && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="block-allday-toggle">
              <input
                type="checkbox"
                checked={formData.all_day}
                onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Todo el día</span>
            </label>
          )}

          {/* Time range */}
          {!formData.all_day && formData.block_type !== 'feriado' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hora inicio</Label>
                <Input
                  type="time"
                  data-testid="block-start-time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="mt-1 bg-transparent border-[#E8E5DD]"
                />
              </div>
              <div>
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  data-testid="block-end-time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="mt-1 bg-transparent border-[#E8E5DD]"
                />
              </div>
            </div>
          )}

          {/* Color */}
          <div>
            <Label>Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer border border-[#E8E5DD]"
              />
              <span className="text-sm text-gray-500">{formData.color}</span>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button
              type="submit"
              data-testid="submit-block-button"
              disabled={loading}
              className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
            >
              {loading ? 'Guardando...' : (block ? 'Actualizar' : 'Crear Bloqueo')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
